import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { useCreateCompany, useUpdateCompany, useUploadLogo } from '@/lib/api/companies';
import type { CreateCompanyRequest, Company } from '@shared/types';

const companySchema = z.object({
  name: z.string().min(2, 'Company name must be at least 2 characters'),
  contactEmail: z.string().email('Invalid email address').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  brandColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid hex color').optional(),
  primaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid hex color').optional().or(z.literal('')),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyOnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  existingCompany?: Company;
}

export function CompanyOnboardingModal({ isOpen, onComplete, existingCompany }: CompanyOnboardingModalProps) {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const isEditMode = !!existingCompany;

  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const uploadLogo = useUploadLogo();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: existingCompany?.name || '',
      contactEmail: existingCompany?.contactEmail || '',
      contactPhone: existingCompany?.contactPhone || '',
      address: existingCompany?.address || '',
      city: existingCompany?.city || '',
      postalCode: existingCompany?.postalCode || '',
      country: existingCompany?.country || '',
      brandColor: existingCompany?.brandColor || '#3B82F6',
      primaryColor: existingCompany?.primaryColor || '',
    },
  });

  // Set logo preview if existing company has a logo
  useEffect(() => {
    if (existingCompany?.logoUrl) {
      setLogoPreview(existingCompany.logoUrl);
    }
  }, [existingCompany]);

  const brandColor = watch('brandColor');
  const primaryColor = watch('primaryColor');

  // Auto-cascade: When brandColor changes, update primaryColor
  useEffect(() => {
    if (brandColor && (!primaryColor || primaryColor === '')) {
      setValue('primaryColor', brandColor);
    }
  }, [brandColor, primaryColor, setValue]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }

      // Validate file type
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(file.type)) {
        toast.error('Logo must be PNG, JPG, or SVG');
        return;
      }

      setLogoFile(file);

      // Generate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      let companyId: string;

      if (isEditMode && existingCompany) {
        // Update existing company
        const company = await updateCompany.mutateAsync({
          companyId: existingCompany.id,
          updates: {
            name: data.name,
            contactEmail: data.contactEmail || null,
            contactPhone: data.contactPhone || null,
            address: data.address || null,
            city: data.city || null,
            postalCode: data.postalCode || null,
            country: data.country || null,
            brandColor: data.brandColor || '#3B82F6',
            primaryColor: data.primaryColor || null,
          },
        });
        companyId = company.id;
        toast.success('Company updated successfully!');
      } else {
        // Create new company
        const request: CreateCompanyRequest = {
          name: data.name,
          contactEmail: data.contactEmail || null,
          contactPhone: data.contactPhone || null,
          address: data.address || null,
          city: data.city || null,
          postalCode: data.postalCode || null,
          country: data.country || null,
          brandColor: data.brandColor || '#3B82F6',
          primaryColor: data.primaryColor || null,
        };

        const company = await createCompany.mutateAsync(request);
        companyId = company.id;
        toast.success('Company created successfully!');
      }

      // Upload logo if provided
      if (logoFile && companyId) {
        await uploadLogo.mutateAsync({
          companyId: companyId,
          file: logoFile,
        });
      }

      onComplete();
    } catch (error) {
      console.error('Failed to save company:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} company. Please try again.`);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isEditMode ? onComplete : () => {}} // Can close if editing, cannot close if creating
      title={isEditMode ? 'Update Your Company' : 'Create Your Company'}
      size="lg"
    >
      <div className="space-y-6">
        <p className="text-content-secondary">
          {isEditMode
            ? 'Update your company profile information below.'
            : "Let's set up your company profile. You'll be the owner and can invite team members later."}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Logo */}
          <div>
            <label className="block text-sm font-medium text-content-primary mb-2">
              Company Logo
            </label>
            <div className="flex items-center gap-4">
              <div
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: logoPreview ? 'transparent' : brandColor || '#3B82F6' }}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoChange}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('logo-upload')?.click()}>
                    Upload Logo
                  </Button>
                </label>
                <p className="text-xs text-content-secondary mt-1">PNG, JPG, or SVG. Max 2MB.</p>
              </div>
            </div>
          </div>

          {/* Company Name */}
          <Input
            label="Company Name"
            {...register('name')}
            error={errors.name?.message}
            placeholder="ACME Corp"
            required
          />

          {/* Brand Color */}
          <div>
            <label className="block text-sm font-medium text-content-primary mb-2">
              Brand Color
            </label>
            <p className="text-xs text-content-secondary mb-2">
              Used for logo placeholder and company branding
            </p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                {...register('brandColor')}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <Input
                {...register('brandColor')}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
            {errors.brandColor && (
              <p className="mt-1 text-sm text-red-600">{errors.brandColor.message}</p>
            )}
          </div>

          {/* Primary UI Color */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-content-primary">
                Primary UI Color
              </label>
              <button
                type="button"
                onClick={() => setValue('primaryColor', '#3B82F6')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Reset to Default
              </button>
            </div>
            <p className="text-xs text-content-secondary mb-2">
              Used throughout the app. Auto-syncs with brand color.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                {...register('primaryColor')}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <Input
                {...register('primaryColor')}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
            {errors.primaryColor && (
              <p className="mt-1 text-sm text-red-600">{errors.primaryColor.message}</p>
            )}
          </div>

          {/* Contact Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Contact Email"
              type="email"
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
              placeholder="contact@company.com"
            />
            <Input
              label="Contact Phone"
              type="tel"
              {...register('contactPhone')}
              error={errors.contactPhone?.message}
              placeholder="+1 (555) 000-0000"
            />
          </div>

          {/* Address */}
          <Input
            label="Address"
            {...register('address')}
            error={errors.address?.message}
            placeholder="123 Main St"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              {...register('city')}
              error={errors.city?.message}
              placeholder="New York"
            />
            <Input
              label="Postal Code"
              {...register('postalCode')}
              error={errors.postalCode?.message}
              placeholder="10001"
            />
            <Input
              label="Country"
              {...register('country')}
              error={errors.country?.message}
              placeholder="USA"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {isEditMode && (
              <Button
                type="button"
                variant="outline"
                onClick={onComplete}
              >
                Skip
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || createCompany.isPending || updateCompany.isPending || uploadLogo.isPending}
            >
              {isSubmitting || createCompany.isPending || updateCompany.isPending || uploadLogo.isPending
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Company' : 'Create Company')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
